import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Clock, TrendingUp, Calculator, DollarSign, Settings, Monitor, Cpu, Code2, Globe,
  ArrowRight, ChevronRight, Sliders, Send, Loader2, CheckCircle, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "process", label: "Prozessoptimierung", icon: Settings, color: "#D4A373", desc: "Manuelle Abläufe automatisieren" },
  { id: "digital", label: "Digitalisierung", icon: Monitor, color: "#3B82F6", desc: "Legacy-Systeme ablösen" },
  { id: "ai", label: "KI / Automatisierung", icon: Cpu, color: "#8B5CF6", desc: "Intelligente Automatisierung" },
  { id: "software", label: "Softwareentwicklung", icon: Code2, color: "#F59E0B", desc: "Massgeschneiderte Lösungen" },
  { id: "web", label: "Web & Marketing", icon: Globe, color: "#EC4899", desc: "Marketing automatisieren" },
];

const SCENARIO_FACTORS = {
  process:  { conservative: 0.20, realistic: 0.30, optimistic: 0.40 },
  digital:  { conservative: 0.25, realistic: 0.40, optimistic: 0.55 },
  ai:       { conservative: 0.35, realistic: 0.55, optimistic: 0.70 },
  software: { conservative: 0.15, realistic: 0.25, optimistic: 0.35 },
  web:      { conservative: 0.20, realistic: 0.30, optimistic: 0.40 },
};

const PROJECT_SIZES = {
  process: {
    S: { label: "Quick Fix", duration: "2–4 Wochen", cost: 10000, ops: 1200, examples: ["Einzelner Prozess automatisieren", "Report-Dashboard bauen", "Formular digitalisieren"] },
    M: { label: "Standard", duration: "4–8 Wochen", cost: 25000, ops: 3000, examples: ["Workflow-Automatisierung", "Qualitätsmanagement-System", "Digitale Checklisten & Rapporte"] },
    L: { label: "Unternehmensweit", duration: "8–16 Wochen", cost: 50000, ops: 6000, examples: ["ERP-Integration", "Multi-Abteilungs-Optimierung", "End-to-End Prozessdigitalisierung"] },
    XL: { label: "Transformation", duration: "16+ Wochen", cost: 100000, ops: 12000, examples: ["Komplette Prozesslandschaft neu", "Standortübergreifende Systeme", "Lean-Transformation"] },
  },
  digital: {
    S: { label: "Quick Fix", duration: "2–4 Wochen", cost: 10000, ops: 1200, examples: ["Legacy-Formular ablösen", "Cloud-Migration einzelner App", "API-Anbindung"] },
    M: { label: "Standard", duration: "4–8 Wochen", cost: 25000, ops: 3000, examples: ["CRM-Einführung", "Website mit Buchungssystem", "Dokumentenmanagement"] },
    L: { label: "Unternehmensweit", duration: "8–16 Wochen", cost: 50000, ops: 6000, examples: ["ERP-System einführen", "Digitaler Workplace", "Multi-System-Integration"] },
    XL: { label: "Transformation", duration: "16+ Wochen", cost: 100000, ops: 12000, examples: ["Komplette Digitalstrategie", "Plattform-Entwicklung", "Unternehmensweite Migration"] },
  },
  ai: {
    S: { label: "Quick Fix", duration: "2–4 Wochen", cost: 12000, ops: 1500, examples: ["Chatbot für FAQ", "Dokumenten-Klassifizierung", "E-Mail-Triage automatisieren"] },
    M: { label: "Standard", duration: "4–8 Wochen", cost: 30000, ops: 3600, examples: ["Predictive Maintenance", "KI-gestützte Angebotserstellung", "Automatisierte Datenanalyse"] },
    L: { label: "Unternehmensweit", duration: "8–16 Wochen", cost: 60000, ops: 7200, examples: ["KI-gestützte Prozessoptimierung", "Machine Learning Pipeline", "Intelligente Automatisierung"] },
    XL: { label: "Transformation", duration: "16+ Wochen", cost: 120000, ops: 14000, examples: ["Unternehmensweite KI-Strategie", "Autonome Agenten-Systeme", "KI-First Transformation"] },
  },
  software: {
    S: { label: "Quick Fix", duration: "2–4 Wochen", cost: 10000, ops: 1200, examples: ["Internes Tool / Dashboard", "Schnittstellen-Adapter", "Prototyp / MVP"] },
    M: { label: "Standard", duration: "4–8 Wochen", cost: 25000, ops: 3000, examples: ["Kundenportal", "Auftragsmanagement-App", "Branchenlösung"] },
    L: { label: "Unternehmensweit", duration: "8–16 Wochen", cost: 50000, ops: 6000, examples: ["SaaS-Plattform", "Multi-Mandanten-System", "Mobile + Web App"] },
    XL: { label: "Transformation", duration: "16+ Wochen", cost: 100000, ops: 12000, examples: ["Enterprise-Plattform", "Marketplace / Ökosystem", "Legacy-Komplettersatz"] },
  },
  web: {
    S: { label: "Quick Fix", duration: "2–4 Wochen", cost: 8000, ops: 1000, examples: ["Landing Page", "SEO-Optimierung", "Newsletter-Automation"] },
    M: { label: "Standard", duration: "4–8 Wochen", cost: 20000, ops: 2400, examples: ["Corporate Website", "Content-Marketing-System", "Social Media Automation"] },
    L: { label: "Unternehmensweit", duration: "8–16 Wochen", cost: 45000, ops: 5000, examples: ["E-Commerce-Shop", "Marketing-Automation-Suite", "Multi-Channel-Strategie"] },
    XL: { label: "Transformation", duration: "16+ Wochen", cost: 90000, ops: 10000, examples: ["Digitale Marketing-Plattform", "Headless Commerce", "Full-Funnel Automation"] },
  },
};

const SIZE_ORDER = ["S", "M", "L", "XL"];

const SCENARIO_LABELS = {
  conservative: { label: "Konservativ", color: "#94A3B8" },
  realistic:    { label: "Realistisch", color: "#10B981" },
  optimistic:   { label: "Optimistisch", color: "#3B82F6" },
};

function formatCHF(n) { return `CHF ${Math.round(n).toLocaleString("de-CH")}`; }

function calc(category, scenario, employees, hoursWeek, hourlyCost, projectCost, annualOpsCost) {
  const ts = SCENARIO_FACTORS[category][scenario];
  const hpy = employees * hoursWeek * 48;
  const hs = Math.round(hpy * ts);
  const cs = Math.round(hs * hourlyCost);
  const nab = cs - annualOpsCost;
  const tc = projectCost + annualOpsCost * 5;
  // ROI aligned with Atlas: (costSaved - projectCost) / projectCost
  const roi = projectCost > 0 ? Math.round(((cs - projectCost) / projectCost) * 100) : 0;
  const paybackMonths = cs > 0 ? Math.round((projectCost / cs) * 12 * 10) / 10 : 0;
  return {
    timeSaving: ts, hoursPerYear: hpy, hoursSaved: hs, costSaved: cs, netAnnualBenefit: nab,
    fiveYearValue: cs * 5 - tc,
    roi,
    paybackMonths,
  };
}

function SliderInput({ label, value, onChange, min, max, step, unit, testId }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState("");
  const startEdit = () => { setEditVal(String(value)); setEditing(true); };
  const commitEdit = () => {
    setEditing(false);
    let num = parseFloat(editVal.replace(/[']/g, ""));
    if (isNaN(num)) return;
    num = Math.max(min, Math.min(max, Math.round(num / step) * step));
    onChange(num);
  };
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-baseline">
        <label className="text-xs font-semibold uppercase text-[#64748B]" style={{ letterSpacing: "0.08em" }}>{label}</label>
        {editing ? (
          <input autoFocus type="text" value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => e.key === "Enter" && commitEdit()}
            className="text-base font-semibold text-[#0F172A] font-playfair bg-transparent border-b-2 border-[#D4A373] outline-none text-right w-24"
          />
        ) : (
          <button onClick={startEdit}
            className="text-base font-semibold text-[#0F172A] font-playfair hover:text-[#D4A373] cursor-text transition-colors"
            data-testid={`${testId}-value`}>
            {value.toLocaleString("de-CH")} {unit}
          </button>
        )}
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-[#E2E8F0] rounded-none appearance-none cursor-pointer accent-[#D4A373]"
        data-testid={testId} />
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const m = typeof label === "number" ? label : 0;
  const y = Math.floor(m / 12);
  const rm = m % 12;
  const tl = y > 0 ? (rm > 0 ? `Jahr ${y}, Monat ${rm}` : `Jahr ${y}`) : `Monat ${m}`;
  return (
    <div className="bg-[#0F172A] text-white text-xs px-3 py-2">
      <div className="font-semibold mb-1">{tl}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#94A3B8]">{p.name}:</span>
          <span className="font-semibold">{formatCHF(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ROIPage() {
  const [step, setStep] = useState(1); // 1=Inputs, 2=Results, 3=Lead
  const [category, setCategory] = useState("process");
  const [scenario, setScenario] = useState("realistic");
  const [employees, setEmployees] = useState(5);
  const [hoursWeek, setHoursWeek] = useState(10);
  const [hourlyCost, setHourlyCost] = useState(65);
  const [projectSize, setProjectSize] = useState("M");

  const sizeData = PROJECT_SIZES[category]?.[projectSize] || PROJECT_SIZES.process.M;
  const projectCost = sizeData.cost;
  const annualOpsCost = sizeData.ops;
  // Lead form
  const [leadFirstName, setLeadFirstName] = useState("");
  const [leadLastName, setLeadLastName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dossierUrl, setDossierUrl] = useState(null);
  const [dossierReady, setDossierReady] = useState(false);
  const [dossierChecking, setDossierChecking] = useState(false);

  const sf = SCENARIO_FACTORS[category];
  const allScenarios = useMemo(() => ({
    conservative: calc(category, "conservative", employees, hoursWeek, hourlyCost, projectCost, annualOpsCost),
    realistic:    calc(category, "realistic", employees, hoursWeek, hourlyCost, projectCost, annualOpsCost),
    optimistic:   calc(category, "optimistic", employees, hoursWeek, hourlyCost, projectCost, annualOpsCost),
  }), [category, employees, hoursWeek, hourlyCost, projectCost, annualOpsCost, projectSize]);

  const active = allScenarios[scenario];

  const breakEvenMonths = useMemo(() => {
    if (active.netAnnualBenefit <= 0) return null;
    const m = Math.round((projectCost / active.netAnnualBenefit) * 12);
    return m <= 60 ? m : null;
  }, [active.netAnnualBenefit, projectCost]);

  const chartData = useMemo(() => {
    const d = [];
    for (let m = 0; m <= 60; m++) {
      const y = m / 12;
      d.push({
        month: m,
        conservative: allScenarios.conservative.costSaved * y - projectCost - annualOpsCost * y,
        realistic: allScenarios.realistic.costSaved * y - projectCost - annualOpsCost * y,
        optimistic: allScenarios.optimistic.costSaved * y - projectCost - annualOpsCost * y,
      });
    }
    return d;
  }, [allScenarios, projectCost, annualOpsCost]);

  const catInfo = CATEGORIES.find(c => c.id === category);

  const goStep = (n) => {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitLead = async () => {
    if (!leadEmail || !leadFirstName || !leadLastName) { toast.error("Bitte Vorname, Nachname und E-Mail eingeben"); return; }
    setSubmitting(true);
    try {
      const fullName = `${leadFirstName} ${leadLastName}`.trim();
      const catLabel = CATEGORIES.find(c => c.id === category)?.label || category;
      const res = await axios.post(`${API}/roi/lead`, {
        first_name: leadFirstName,
        last_name: leadLastName,
        name: fullName,
        email: leadEmail,
        phone: leadPhone,
        company: leadCompany,
        category,
        category_label: catLabel,
        scenario,
        inputs: { employees, hours_week: hoursWeek, hourly_cost: hourlyCost, project_cost: projectCost, annual_ops_cost: annualOpsCost, project_size: projectSize, project_size_label: sizeData.label },
        scenario_factors: { conservative: Math.round(sf.conservative * 100), realistic: Math.round(sf.realistic * 100), optimistic: Math.round(sf.optimistic * 100) },
        results: {
          conservative: { efficiency_pct: Math.round(sf.conservative * 100), ...allScenarios.conservative },
          realistic: { efficiency_pct: Math.round(sf.realistic * 100), ...allScenarios.realistic },
          optimistic: { efficiency_pct: Math.round(sf.optimistic * 100), ...allScenarios.optimistic },
        },
        chart_data: [0, 12, 24, 36, 48, 60].map(m => {
          const y = m / 12;
          return { month: m, year: y,
            conservative: allScenarios.conservative.costSaved * y - projectCost - annualOpsCost * y,
            realistic: allScenarios.realistic.costSaved * y - projectCost - annualOpsCost * y,
            optimistic: allScenarios.optimistic.costSaved * y - projectCost - annualOpsCost * y,
          };
        }),
        break_even_months: breakEvenMonths,
        client_name: leadCompany || fullName,
      });
      setSubmitted(true);
      if (res.data.dossier_url) {
        setDossierUrl(res.data.dossier_url);
        setDossierChecking(true);
        // Poll until PDF is ready (Atlas generates async)
        const checkPdf = async (url, attempts = 0) => {
          if (attempts > 15) { setDossierChecking(false); setDossierReady(true); return; } // give up after ~30s, show anyway
          try {
            const check = await axios.head(url);
            if (check.status === 200) { setDossierReady(true); setDossierChecking(false); return; }
          } catch { /* PDF not ready yet */ }
          setTimeout(() => checkPdf(url, attempts + 1), 2000);
        };
        checkPdf(res.data.dossier_url);
      }
    } catch {
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-lab-bg-light" data-testid="roi-page">
      <Navigation />
      <main>
        {/* Hero */}
        <section className="pt-24 pb-16 bg-[#0F172A]">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="pt-12">
              <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#D4A373] transition-colors text-sm mb-8" data-testid="back-to-home">
                <ArrowRight className="w-4 h-4 rotate-180" /> Zurück zur Startseite
              </Link>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
                  <Calculator className="w-7 h-7 text-[#D4A373]" />
                </div>
                <div>
                  <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">ROI-Rechner</h1>
                  <p className="text-gray-400 mt-1">Einsparungspotenzial berechnen — in unter 2 Minuten</p>
                </div>
              </div>
            </div>
            {/* Step indicator */}
            <div className="flex items-center gap-3 mt-4 pb-4">
              {[
                { n: 1, label: "Konfiguration" },
                { n: 2, label: "Ergebnis" },
                { n: 3, label: "Analyse anfordern" },
              ].map(({ n, label }) => (
                <button key={n} onClick={() => n < step ? goStep(n) : null}
                  className={`flex items-center gap-2 text-xs font-semibold transition-all ${
                    step >= n ? "text-white" : "text-[#475569]"
                  } ${n < step ? "cursor-pointer hover:text-[#D4A373]" : "cursor-default"}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[0.65rem] font-bold ${
                    step > n ? "bg-[#D4A373] text-white" : step === n ? "bg-white text-[#0F172A]" : "bg-[#334155] text-[#64748B]"
                  }`}>{n}</span>
                  <span className="hidden sm:inline">{label}</span>
                  {n < 3 && <ChevronRight className="w-3 h-3 text-[#475569]" />}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-lab-bg-light">
          <div className="max-w-5xl mx-auto px-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Configuration */}
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                  {/* Category */}
                  <div>
                    <h2 className="text-xs font-bold uppercase text-[#94A3B8] mb-4" style={{ letterSpacing: "0.15em" }}>Welchen Bereich möchten Sie optimieren?</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {CATEGORIES.map(cat => {
                        const CatIcon = cat.icon;
                        return (
                          <button key={cat.id} onClick={() => setCategory(cat.id)}
                            className={`p-4 border text-left transition-all ${
                              category === cat.id ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#CBD5E1] bg-white text-[#0F172A] hover:border-[#D4A373]"
                            }`}
                            data-testid={`roi-public-cat-${cat.id}`}>
                            <CatIcon className="w-5 h-5 mb-2" style={{ color: cat.color }} />
                            <div className="text-sm font-semibold">{cat.label}</div>
                            <div className="text-[0.6rem] text-[#94A3B8] mt-0.5">{cat.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sliders */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-[#CBD5E1] p-6 space-y-5">
                      <SliderInput label="Betroffene Mitarbeitende" value={employees} onChange={setEmployees}
                        min={1} max={50} step={1} unit="MA" testId="roi-pub-employees" />
                      <SliderInput label="Stunden/Woche pro MA (manuelle Aufgaben)" value={hoursWeek} onChange={setHoursWeek}
                        min={1} max={40} step={1} unit="h" testId="roi-pub-hours" />
                      <SliderInput label="Stundenkosten (Durchschnitt)" value={hourlyCost} onChange={setHourlyCost}
                        min={40} max={200} step={5} unit="CHF" testId="roi-pub-cost" />
                    </div>
                    <div className="bg-white border border-[#CBD5E1] p-6 space-y-4">
                      <div className="flex items-baseline justify-between">
                        <label className="text-xs font-semibold uppercase text-[#64748B]" style={{ letterSpacing: "0.08em" }}>Projektgrösse wählen</label>
                        <span className="text-[11px] text-[#D4A373] font-medium">Empfohlen: {employees <= 3 ? "S" : employees <= 10 ? "M" : employees <= 25 ? "L" : "XL"}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {SIZE_ORDER.map(size => {
                          const sd = PROJECT_SIZES[category]?.[size];
                          if (!sd) return null;
                          const isActive = projectSize === size;
                          const recommended = (size === "S" && employees <= 3) || (size === "M" && employees > 3 && employees <= 10) || (size === "L" && employees > 10 && employees <= 25) || (size === "XL" && employees > 25);
                          return (
                            <button
                              key={size}
                              onClick={() => setProjectSize(size)}
                              className={`text-left p-3 border transition-all relative ${isActive ? "border-[#D4A373] bg-[#D4A373]/5" : "border-[#E2E8F0] hover:border-[#D4A373]/50"}`}
                              data-testid={`roi-size-${size}`}
                            >
                              {recommended && !isActive && <span className="absolute -top-2 right-2 text-[9px] bg-[#D4A373] text-white px-1.5 py-0.5 font-semibold">Empfohlen</span>}
                              <div className="flex items-baseline gap-2 mb-1">
                                <span className={`font-playfair text-lg font-semibold ${isActive ? "text-[#D4A373]" : "text-[#0F172A]"}`}>{size}</span>
                                <span className="text-xs text-[#64748B]">{sd.label}</span>
                              </div>
                              <div className="text-[10px] text-[#94A3B8] mb-1.5">{sd.duration}</div>
                              <div className="text-[10px] text-[#94A3B8] font-semibold mb-1">Beispiele:</div>
                              <ul className="space-y-0.5">
                                {sd.examples.map((ex, j) => (
                                  <li key={j} className="text-[11px] text-[#64748B] leading-tight">· {ex}</li>
                                ))}
                              </ul>
                            </button>
                          );
                        })}
                      </div>
                      {/* Quick ROI preview */}
                      <div className="pt-3 border-t border-[#E2E8F0]">
                        <div className="text-xs text-[#94A3B8] mb-1">Geschätzter ROI ({SCENARIO_LABELS[scenario].label})</div>
                        <div className="font-playfair text-3xl font-semibold" style={{ color: active.roi > 0 ? "#10B981" : "#EF4444" }}>
                          {active.roi > 0 ? "+" : ""}{active.roi}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <Link to="/"
                      className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">
                      Zurück zur Startseite
                    </Link>
                    <button onClick={() => goStep(2)}
                      className="flex items-center gap-2 px-6 py-3 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors"
                      data-testid="roi-pub-next">
                      Ergebnis anzeigen <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Results */}
              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                  {/* Scenario toggle */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Sliders className="w-4 h-4 text-[#64748B]" />
                    <span className="text-xs font-bold uppercase text-[#94A3B8] mr-2" style={{ letterSpacing: "0.1em" }}>Szenario</span>
                    {Object.entries(SCENARIO_LABELS).map(([key, { label, color }]) => (
                      <button key={key} onClick={() => setScenario(key)}
                        className={`px-3 py-1.5 text-xs font-semibold border transition-all ${
                          scenario === key ? "text-white border-transparent" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#CBD5E1]"
                        }`}
                        style={scenario === key ? { backgroundColor: color } : {}}
                        data-testid={`roi-pub-scenario-${key}`}>
                        {label} ({Math.round(sf[key] * 100)}%)
                      </button>
                    ))}
                  </div>

                  {/* Main results */}
                  <div className="bg-[#0F172A] p-8 border border-[#1E293B]">
                    <div className="flex items-center gap-2 mb-6">
                      <span className="w-2 h-2 rounded-full" style={{ background: catInfo.color }} />
                      <h2 className="text-xs font-bold uppercase text-[#94A3B8]" style={{ letterSpacing: "0.15em" }}>
                        {catInfo.label} — {SCENARIO_LABELS[scenario].label}
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white border border-[#CBD5E1] p-5 text-center">
                        <Clock className="w-5 h-5 mx-auto mb-2 text-[#D4A373]" />
                        <div className="text-[0.6rem] font-bold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.1em" }}>Zeitersparnis</div>
                        <div className="font-playfair text-2xl font-semibold text-[#0F172A]">{active.hoursSaved.toLocaleString("de-CH")} h</div>
                        <div className="text-xs text-[#64748B]">pro Jahr</div>
                      </div>
                      <div className="bg-white border border-[#CBD5E1] p-5 text-center">
                        <DollarSign className="w-5 h-5 mx-auto mb-2 text-[#10B981]" />
                        <div className="text-[0.6rem] font-bold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.1em" }}>Netto-Ersparnis</div>
                        <div className="font-playfair text-2xl font-semibold text-[#0F172A]">{formatCHF(active.netAnnualBenefit)}</div>
                        <div className="text-xs text-[#64748B]">pro Jahr</div>
                      </div>
                      <div className="bg-white border border-[#CBD5E1] p-5 text-center">
                        <TrendingUp className="w-5 h-5 mx-auto mb-2" style={{ color: active.roi > 0 ? "#10B981" : "#EF4444" }} />
                        <div className="text-[0.6rem] font-bold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.1em" }}>ROI</div>
                        <div className="font-playfair text-2xl font-semibold text-[#0F172A]">{active.roi > 0 ? "+" : ""}{active.roi}%</div>
                        <div className="text-xs text-[#64748B]">im ersten Jahr</div>
                      </div>
                      <div className="bg-white border border-[#CBD5E1] p-5 text-center">
                        <Calculator className="w-5 h-5 mx-auto mb-2 text-[#3B82F6]" />
                        <div className="text-[0.6rem] font-bold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.1em" }}>Amortisation</div>
                        <div className="font-playfair text-2xl font-semibold text-[#0F172A]">{active.paybackMonths} Mt.</div>
                        <div className="text-xs text-[#64748B]">{active.paybackMonths <= 12 ? "Unter 1 Jahr" : `${(active.paybackMonths / 12).toFixed(1)} Jahre`}</div>
                      </div>
                    </div>
                    <div className="text-center mt-6 pt-5 border-t border-[#334155]">
                      <div className="text-[0.6rem] font-bold uppercase text-[#64748B]" style={{ letterSpacing: "0.15em" }}>5-Jahres-Wert</div>
                      <div className={`font-playfair text-4xl font-semibold mt-1 ${active.fiveYearValue > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                        {formatCHF(active.fiveYearValue)}
                      </div>
                    </div>
                  </div>

                  {/* Scenario comparison */}
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(SCENARIO_LABELS).map(([key, { label, color }]) => {
                      const sc = allScenarios[key];
                      return (
                        <button key={key} onClick={() => setScenario(key)}
                          className={`p-4 border text-center transition-all ${scenario === key ? "border-2" : "border-[#E2E8F0] hover:border-[#CBD5E1]"} bg-white`}
                          style={scenario === key ? { borderColor: color } : {}}>
                          <div className="text-[0.6rem] font-bold uppercase mb-1" style={{ color, letterSpacing: "0.1em" }}>{label}</div>
                          <div className="font-playfair text-xl font-semibold text-[#0F172A]">{sc.roi > 0 ? "+" : ""}{sc.roi}%</div>
                          <div className="text-xs text-[#64748B]">{formatCHF(sc.fiveYearValue)} / 5 Jahre</div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Chart */}
                  <div className="bg-white border border-[#CBD5E1] p-6" data-testid="roi-pub-chart">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-xs font-bold uppercase text-[#94A3B8]" style={{ letterSpacing: "0.15em" }}>5-Jahres-Projektion</h3>
                      {breakEvenMonths && (
                        <div className="text-xs font-semibold" style={{ color: SCENARIO_LABELS[scenario].color }}>
                          Break-Even nach {breakEvenMonths} Monaten
                        </div>
                      )}
                    </div>
                    <div style={{ width: "100%", height: 280 }}>
                      <ResponsiveContainer>
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                          <defs>
                            <linearGradient id="pubRealGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0.01} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="month" type="number" domain={[0, 60]} ticks={[0, 12, 24, 36, 48, 60]}
                            tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={v => `Jahr ${v / 12}`}
                            axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={v => `${Math.round(v / 1000)}k`}
                            axisLine={false} tickLine={false} width={50} />
                          <Tooltip content={<ChartTooltip />} />
                          <ReferenceLine y={0} stroke="#CBD5E1" strokeWidth={1} />
                          <Area type="monotone" dataKey="optimistic" name="Optimistisch" stroke="#3B82F6" strokeWidth={scenario === "optimistic" ? 2.5 : 1} fill="none" strokeDasharray={scenario === "optimistic" ? "0" : "4 3"} />
                          <Area type="monotone" dataKey="realistic" name="Realistisch" stroke="#10B981" strokeWidth={scenario === "realistic" ? 2.5 : 1} fill="url(#pubRealGrad)" strokeDasharray={scenario === "realistic" ? "0" : "4 3"} />
                          <Area type="monotone" dataKey="conservative" name="Konservativ" stroke="#94A3B8" strokeWidth={scenario === "conservative" ? 2.5 : 1} fill="none" strokeDasharray={scenario === "conservative" ? "0" : "4 3"} />
                          {breakEvenMonths && (
                            <ReferenceLine x={breakEvenMonths} stroke="#D4A373" strokeWidth={1.5} strokeDasharray="4 4"
                              label={{ value: `Break-Even (${breakEvenMonths} Mt.)`, position: "top", fontSize: 10, fill: "#D4A373", fontWeight: 600 }} />
                          )}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-6 mt-3">
                      {Object.entries(SCENARIO_LABELS).map(([key, { label, color }]) => (
                        <button key={key} onClick={() => setScenario(key)}
                          className={`flex items-center gap-2 text-xs ${scenario === key ? "font-semibold" : "text-[#94A3B8]"}`}
                          style={scenario === key ? { color } : {}}>
                          <span className="w-3 h-0.5 inline-block" style={{ backgroundColor: color }} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <button onClick={() => goStep(1)}
                      className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">
                      Zurück anpassen
                    </button>
                    <button onClick={() => goStep(3)}
                      className="flex items-center gap-2 px-6 py-3 bg-[#D4A373] text-white text-sm font-semibold hover:bg-[#C4956A] transition-colors"
                      data-testid="roi-pub-get-pdf">
                      Detaillierte Analyse anfordern <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Lead capture */}
              {step === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  {!submitted ? (
                    <div className="max-w-lg mx-auto space-y-6">
                      <div className="text-center mb-8">
                        <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-2">
                          Ihre persönliche ROI-Analyse
                        </h2>
                        <p className="text-sm text-[#64748B]">
                          Erhalten Sie ein professionelles PDF-Dossier mit Ihrer individuellen Berechnung — kostenlos.
                        </p>
                      </div>

                      {/* Summary box */}
                      <div className="bg-[#0F172A] p-5 text-center">
                        <div className="text-xs text-[#94A3B8] mb-1">{catInfo.label} — {SCENARIO_LABELS[scenario].label}</div>
                        <div className="font-playfair text-3xl font-semibold text-[#10B981]">
                          {active.roi > 0 ? "+" : ""}{active.roi}% ROI
                        </div>
                        <div className="text-xs text-[#64748B] mt-1">{formatCHF(active.fiveYearValue)} über 5 Jahre</div>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input value={leadFirstName} onChange={e => setLeadFirstName(e.target.value)}
                            className="border border-[#CBD5E1] px-4 py-3 text-sm focus:border-[#D4A373] outline-none"
                            placeholder="Vorname *" data-testid="roi-pub-firstname" />
                          <input value={leadLastName} onChange={e => setLeadLastName(e.target.value)}
                            className="border border-[#CBD5E1] px-4 py-3 text-sm focus:border-[#D4A373] outline-none"
                            placeholder="Nachname *" data-testid="roi-pub-lastname" />
                        </div>
                        <input value={leadCompany} onChange={e => setLeadCompany(e.target.value)}
                          className="w-full border border-[#CBD5E1] px-4 py-3 text-sm focus:border-[#D4A373] outline-none"
                          placeholder="Firma (optional)" data-testid="roi-pub-company" />
                        <input value={leadEmail} onChange={e => setLeadEmail(e.target.value)} type="email"
                          className="w-full border border-[#CBD5E1] px-4 py-3 text-sm focus:border-[#D4A373] outline-none"
                          placeholder="E-Mail-Adresse *" data-testid="roi-pub-email" />
                        <input value={leadPhone} onChange={e => setLeadPhone(e.target.value)} type="tel"
                          className="w-full border border-[#CBD5E1] px-4 py-3 text-sm focus:border-[#D4A373] outline-none"
                          placeholder="Telefon (optional)" data-testid="roi-pub-phone" />
                      </div>
                      <button onClick={submitLead} disabled={submitting || !leadEmail || !leadFirstName || !leadLastName}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors disabled:opacity-50"
                        data-testid="roi-pub-submit">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {submitting ? "Wird gesendet..." : "ROI-Dossier anfordern"}
                      </button>
                      <p className="text-[0.65rem] text-[#94A3B8] text-center">
                        Mit dem Absenden stimmen Sie unserer <Link to="/datenschutz" className="underline hover:text-[#D4A373]">Datenschutzerklärung</Link> zu.
                      </p>
                      <button onClick={() => goStep(2)}
                        className="block mx-auto text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">
                        Zurück zum Ergebnis
                      </button>
                    </div>
                  ) : (
                    <div className="max-w-lg mx-auto text-center space-y-6">
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
                        <CheckCircle className="w-16 h-16 mx-auto text-[#10B981]" />
                      </motion.div>
                      <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
                        Vielen Dank, {leadFirstName}!
                      </h2>
                      <p className="text-sm text-[#64748B]">
                        {dossierUrl && dossierReady
                          ? "Ihr ROI-Dossier ist bereit."
                          : dossierUrl && dossierChecking
                          ? "Ihr Dossier wird erstellt..."
                          : "Wir erstellen Ihr persönliches ROI-Dossier und senden es Ihnen in Kürze per E-Mail."}
                      </p>
                      {dossierUrl && dossierChecking && (
                        <div className="flex items-center justify-center gap-3 py-4">
                          <div className="w-5 h-5 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-[#64748B]">PDF wird generiert...</span>
                        </div>
                      )}
                      {dossierUrl && dossierReady && (
                        <a href={dossierUrl} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#10B981] text-white text-sm font-semibold hover:bg-[#059669] transition-colors"
                          data-testid="roi-pub-download">
                          <ExternalLink className="w-4 h-4" /> PDF herunterladen
                        </a>
                      )}
                      <div className="pt-4">
                        <Link to="/#section-scheduler"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors">
                          Erstgespräch vereinbaren <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
